import {
	AfterViewInit,
	Directive,
	ElementRef,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output, SimpleChanges
} from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { resizeObserver$ } from "../../../utils/resize.halper";

export type HiddenItemsData= {hiddenItemNum:number;partlyHiddenItemIndex:number};

/**
 *  @ngModule RISCommonModule
 *  @description
 *  Returns the number of the container's child elements that are not shown due to height overflow of the content.
 *  This event get triggerd if the  container height changed and or num of child element changed.
 *  notifyChildNumChanged() method can be called be the code client (The directive user) in case there was a change in
 *  the number of child elements some time after the view was already renderd and there was no window resize event.<br>>
 *  **note in order for this directive to work properly** -
 *  the user of this directive should take care to provide values to the input parameters.<br>
 *  regarding CSS :<br>
 *  the hosting element should be defined as position:relative.<br>
 *  the container that is used to display the hidden items result should be defined as display:absolute
 *  @param elementsNum the total num of the container child elements (typcally sould get its value from binding to a variable).
 *  @param resultRegionHeight the total height in pixels of the container element that is used to display the hidden item/s count. integer value
 *  @event hiddenExternalItemsNum triggerd onAfterViewInit(of the hosting component) , when the value of the elementsNum input parameter changes
 *  ,on the hosting container element resize and on window resize.
 *  @see  https://angular.io/guide/lifecycle-hooks  for more info on onAfterViewInit
 *
 * <div class="icon-nav-favorites-wrapper" #iconNavFavoritesWrapper cpHiddenExternalItemsNum
 * 						 [resultRegionHeight]="40"
 * 						 [elementsNum]="worklistNavigationIcons.length"
 * 						 (hiddenExternalItemsNum)="updateHiddenItemsNum($event)">
 */

@UntilDestroy()
@Directive({
	selector: '[cpHiddenExternalItemsNum]',
})
export class HiddenExternalItemsNumDirective implements AfterViewInit, OnDestroy,OnChanges,OnInit {
	@Input() elementsNum:number;
	@Input() resultRegionHeight = 0;
	@Output() hiddenExternalItemsNum: EventEmitter<HiddenItemsData> = new EventEmitter<HiddenItemsData>();

	private lastTotalChildrenNum: number;
	private lastContainerContentHeight: number;
	private heightResizeObservable$;
	private partlyHiddenItemIndex;
	constructor(public elementRef: ElementRef) {
		this.lastTotalChildrenNum = 0;
		this.lastContainerContentHeight = 0;
	}

	public ngOnInit(){
		this.heightResizeObservable$ = resizeObserver$(this.elementRef.nativeElement);
		this.heightResizeObservable$.pipe(untilDestroyed(this)).subscribe((x:ResizeObserverEntry)=> {
			//console.log(x);
			this.getHiddenExternalItemsNum(undefined, x?.[0]);
		});
	}

	public ngOnChanges(changes: SimpleChanges) {
		if (changes['elementsNum'] && !changes['elementsNum'].firstChange && changes['elementsNum'].currentValue !== changes['elementsNum'].previousValue){
			this.notifyChildNumChanged(changes['elementsNum'].currentValue);
		}
	}

	public ngAfterViewInit(): void {
		this.getHiddenExternalItemsNum();
	}

	public ngOnDestroy(): void {
	}

	public notifyChildNumChanged(numOfChildElements?:number){
		this.getHiddenExternalItemsNum(numOfChildElements);
	}

	private getHiddenExternalItemsNum(numOfChildElements?:number, resizeEvent?:ResizeObserverEntry) {
		this.partlyHiddenItemIndex = -1;
		let totalChildrenNum:number;
		//if items num was passed in as an argument
		if (typeof numOfChildElements !== 'undefined' && numOfChildElements !== null){
			totalChildrenNum = numOfChildElements;
		}else{
			totalChildrenNum = this.getChildCount();
		}
		let containerContentHeight:number;
		if (resizeEvent){
			containerContentHeight = resizeEvent.contentRect.height;
		}else{
			containerContentHeight = this.getContainerContentHeight();
		}

		if (totalChildrenNum !== this.lastTotalChildrenNum || containerContentHeight !== this.lastContainerContentHeight) {
			let hiddenCounter:number;
			if (totalChildrenNum > 0) {
				const firstChildElementRef = this.elementRef.nativeElement?.children[0]?.children[0];
				if (firstChildElementRef){
					const itemOuterHeight = this.getElementOuterHeight(firstChildElementRef);
					hiddenCounter = this.calcHiddenItemsNum(containerContentHeight,itemOuterHeight,totalChildrenNum,firstChildElementRef);
				}
			}else{
				hiddenCounter=0;
			}
			this.lastTotalChildrenNum = totalChildrenNum;
			this.lastContainerContentHeight = containerContentHeight;
			this.hiddenExternalItemsNum.emit({hiddenItemNum:hiddenCounter,partlyHiddenItemIndex:this.partlyHiddenItemIndex});
		}
	}

	private calcHiddenItemsNum(containerContentHeight:number,referenceElemOuterHeight:number,totalChildrenNum:number,childElementRef:HTMLElement){
		const heightOfFullyDisplayedItem = referenceElemOuterHeight - parseFloat(window.getComputedStyle(childElementRef).
		getPropertyValue('margin-bottom')) - parseFloat(window.getComputedStyle(childElementRef).
		getPropertyValue('padding-bottom'));
		let displayedElementsNum = Math.floor(containerContentHeight / referenceElemOuterHeight);
		let remainder = containerContentHeight % referenceElemOuterHeight;
		if (remainder >= heightOfFullyDisplayedItem){
			displayedElementsNum++;
		}
		let hiddenElementesNum = (totalChildrenNum >= displayedElementsNum) ? (totalChildrenNum - displayedElementsNum) : 0;

		//If there are hidden elements we need to take into account the height of the num of items display container
		if (hiddenElementesNum >  0 ){
			let containerHeightExcludingResultRegion = containerContentHeight - this.resultRegionHeight;
			displayedElementsNum = Math.floor(containerHeightExcludingResultRegion / referenceElemOuterHeight);
			displayedElementsNum = (displayedElementsNum > 0)? displayedElementsNum:0;
			if (containerContentHeight > this.resultRegionHeight) {
				remainder = containerHeightExcludingResultRegion % referenceElemOuterHeight;
				if (remainder >= heightOfFullyDisplayedItem) {
					displayedElementsNum++;
				}else{
					if (remainder>0){
						//this.partlyHiddenItem.emit(displayedElementsNum);
						this.partlyHiddenItemIndex = displayedElementsNum;
					}
				}
			}
			hiddenElementesNum = totalChildrenNum - displayedElementsNum;
		}else{
			this.partlyHiddenItemIndex = -1;
		}
		return hiddenElementesNum;
	}


	private getContainerContentHeight(){
		let elemStyle = window.getComputedStyle(this.elementRef.nativeElement);
		let height = parseFloat(elemStyle.getPropertyValue("height"));
		let boxSizing =elemStyle.getPropertyValue("box-sizing");
		//console.log('boxSizing val  ', boxSizing);
		if (boxSizing==="border-box"){
			let paddingY = parseFloat(elemStyle.paddingTop) + parseFloat(elemStyle.paddingBottom);
			let heightWithoutPadding = height - paddingY;
			//console.log('border-box calc Height no padding ', heightWithoutPadding);
			height = heightWithoutPadding;
		}
		return height;
	}

	private getElementOuterHeight(element:HTMLElement ){
		let elHeight = element.offsetHeight;
		elHeight += parseFloat(window.getComputedStyle(element).getPropertyValue('margin-top'));
		elHeight += parseFloat(window.getComputedStyle(element).getPropertyValue('margin-bottom'));
		return elHeight;
	}

	private getChildCount() {
		return  (this.elementRef?.nativeElement?.children?.length) ? this.elementRef.nativeElement.children.length : 0;
	}

}
